// frontend/src/components/TicketActionButtons/index.js
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { Chip, IconButton, Tooltip } from "@material-ui/core";
import {
  MoreVert,
  Replay,
  Phone,
  CallEnd,
  Mic,
  MicOff,
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  actionButtons: {
    marginRight: 6,
    flex: "none",
    alignSelf: "center",
    marginLeft: "auto",
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  callPanel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 6px",
    borderRadius: 20,
    backgroundColor: theme.palette.action.hover,
  },
  callChip: {
    fontVariantNumeric: "tabular-nums",
  },
  ringing: {
    animation: "$pulse 1.2s ease-in-out infinite",
  },
  "@keyframes pulse": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.35 },
    "100%": { opacity: 1 },
  },
  endCallButton: {
    color: theme.palette.error.main,
  },
}));

const formatDuration = totalSeconds => {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const onlyDigits = s => (s || "").toString().replace(/\D/g, "");

const TARGET_SAMPLE_RATE = 16000;
const FRAME_SIZE = 320;
const PLAYBACK_LEAD_SEC = 0.05;
const PLAYBACK_MAX_LAG_SEC = 0.35;

const downsampleBuffer = (buffer, inputSampleRate, outputSampleRate) => {
  if (outputSampleRate === inputSampleRate) {
    return buffer;
  }

  if (outputSampleRate > inputSampleRate) {
    return buffer;
  }

  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0;
    let count = 0;

    for (
      let i = Math.round(offsetBuffer);
      i < nextOffsetBuffer && i < buffer.length;
      i += 1
    ) {
      accum += buffer[i];
      count += 1;
    }

    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }

  return result;
};

const toInt16Buffer = samples => {
  const pcm16 = new Int16Array(samples.length);

  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i] || 0));
    pcm16[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
  }

  return pcm16.buffer;
};

const parseIncomingPcm16 = chunk => {
  if (!chunk) return null;

  let bytes;

  if (chunk.type === "Buffer" && Array.isArray(chunk.data)) {
    bytes = Uint8Array.from(chunk.data);
  } else if (chunk instanceof ArrayBuffer) {
    bytes = new Uint8Array(chunk);
  } else if (ArrayBuffer.isView(chunk)) {
    bytes = new Uint8Array(chunk.buffer, chunk.byteOffset, chunk.byteLength);
  } else if (Array.isArray(chunk)) {
    bytes = Uint8Array.from(chunk);
  } else {
    return null;
  }

  const sampleCount = Math.floor(bytes.byteLength / 2);
  if (!sampleCount) return null;

  // O socket entrega bytes com byteOffset arbitrário e Int16Array exige alinhamento par.
  const samples = new Int16Array(sampleCount);
  const view = new DataView(bytes.buffer, bytes.byteOffset, sampleCount * 2);

  for (let i = 0; i < sampleCount; i += 1) {
    samples[i] = view.getInt16(i * 2, true);
  }

  return samples;
};

const int16ToFloat32 = chunk => {
  const out = new Float32Array(chunk.length);

  for (let i = 0; i < chunk.length; i += 1) {
    out[i] = chunk[i] / 0x7fff;
  }

  return out;
};

const TicketActionButtons = ({ ticket, socket }) => {
  const classes = useStyles();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calling, setCalling] = useState(false);
  const [callState, setCallState] = useState("idle");
  const [callId, setCallId] = useState(null);
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const ticketOptionsMenuOpen = Boolean(anchorEl);
  const { user } = useContext(AuthContext);

  const streamRef = useRef(null);
  const inputContextRef = useRef(null);
  const sourceRef = useRef(null);
  const processorRef = useRef(null);
  const silentGainRef = useRef(null);
  const pendingRef = useRef([]);
  const outputContextRef = useRef(null);
  const nextPlayTimeRef = useRef(0);
  const mutedRef = useRef(false);
  const callStateRef = useRef("idle");

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  const handleOpenTicketOptionsMenu = e => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseTicketOptionsMenu = () => {
    setAnchorEl(null);
  };

  const handleUpdateTicketStatus = async (e, status, userId) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: status,
        userId: userId || null,
      });

      setLoading(false);
      if (status === "open") {
        history.push(`/tickets/${ticket.id}`);
      } else {
        history.push("/tickets");
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const stopMicrophoneStreaming = async ({ keepStream } = {}) => {
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (silentGainRef.current) {
      silentGainRef.current.disconnect();
      silentGainRef.current = null;
    }

    if (streamRef.current) {
      // Uma track parada passa a gerar silêncio; o stream da ligação atual
      // não pode ser encerrado aqui.
      if (streamRef.current !== keepStream) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      streamRef.current = null;
    }

    if (inputContextRef.current) {
      try {
        await inputContextRef.current.close();
      } catch (err) {
        // noop
      }
      inputContextRef.current = null;
    }

    pendingRef.current = [];
  };

  const ensureOutputContext = () => {
    const existing = outputContextRef.current;
    if (existing && existing.state !== "closed") {
      if (existing.state === "suspended") {
        existing.resume().catch(() => {});
      }
      return existing;
    }

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;

    outputContextRef.current = new AudioCtx();
    nextPlayTimeRef.current = outputContextRef.current.currentTime;
    return outputContextRef.current;
  };

  const playRemoteChunk = chunk => {
    const pcm16 = parseIncomingPcm16(chunk);
    if (!pcm16 || !pcm16.length) return;

    const context = ensureOutputContext();
    if (!context) return;

    const floatChunk = int16ToFloat32(pcm16);
    const audioBuffer = context.createBuffer(1, floatChunk.length, TARGET_SAMPLE_RATE);
    audioBuffer.copyToChannel(floatChunk, 0);

    const source = context.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(context.destination);

    const now = context.currentTime + PLAYBACK_LEAD_SEC;
    const scheduled = nextPlayTimeRef.current;
    // Sem teto, qualquer excedente de chunks empurra a fila e a latência cresce
    // sem limite; abaixo do piso o áudio sai picotado.
    const startAt =
      Number.isFinite(scheduled) && scheduled > now
        ? Math.min(scheduled, context.currentTime + PLAYBACK_MAX_LAG_SEC)
        : now;
    source.start(startAt);
    nextPlayTimeRef.current = startAt + audioBuffer.duration;
  };

  const acquireMicrophone = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(
        "Microfone indisponível neste navegador. Use HTTPS e um navegador compatível."
      );
    }

    const media = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    });

    streamRef.current = media;
    return media;
  };

  const waitForSocket = (timeoutMs = 8000) =>
    new Promise((resolve, reject) => {
      if (!socket) {
        reject(new Error("Conexão em tempo real indisponível. Recarregue a página."));
        return;
      }

      if (socket.connected) {
        resolve();
        return;
      }

      const timer = setTimeout(() => {
        socket.off("connect", onConnect);
        reject(
          new Error("Não foi possível conectar ao servidor de áudio. Tente novamente.")
        );
      }, timeoutMs);

      const onConnect = () => {
        clearTimeout(timer);
        socket.off("connect", onConnect);
        resolve();
      };

      socket.on("connect", onConnect);
      socket.connect();
    });

  const startMicrophoneStreaming = async media => {
    await waitForSocket();

    // Uma captura remanescente duplicaria o uplink e saturaria o encoder.
    await stopMicrophoneStreaming({ keepStream: media });

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const context = new AudioCtx();

    if (context.state === "suspended") {
      await context.resume();
    }

    const source = context.createMediaStreamSource(media);
    const processor = context.createScriptProcessor(4096, 1, 1);
    const silentGain = context.createGain();
    silentGain.gain.value = 0;

    source.connect(processor);
    processor.connect(silentGain);
    silentGain.connect(context.destination);

    pendingRef.current = [];

    processor.onaudioprocess = event => {
      const input = event.inputBuffer.getChannelData(0);
      const downsampled = downsampleBuffer(input, context.sampleRate, TARGET_SAMPLE_RATE);
      const pending = pendingRef.current;

      for (let i = 0; i < downsampled.length; i += 1) {
        pending.push(downsampled[i]);
      }

      // O callback já entrega ~85ms de áudio: um único emit com todos os frames
      // completos evita ~50 eventos/s de socket sem adicionar latência.
      const frameCount = Math.floor(pending.length / FRAME_SIZE);
      if (frameCount < 1) return;

      const batch = pending.splice(0, frameCount * FRAME_SIZE);
      socket.emit("call:audio", {
        ticketId: ticket.id,
        // Mudo envia silêncio para não interromper o fluxo do codec.
        chunk: toInt16Buffer(mutedRef.current ? batch.map(() => 0) : batch),
      });
    };

    streamRef.current = media;
    inputContextRef.current = context;
    sourceRef.current = source;
    processorRef.current = processor;
    silentGainRef.current = silentGain;
  };

  const resetCallState = async () => {
    await stopMicrophoneStreaming();
    nextPlayTimeRef.current = 0;
    mutedRef.current = false;
    setMuted(false);
    setElapsed(0);
    setCalling(false);
    setCallState("idle");
    setCallId(null);
  };

  const handleToggleMute = () => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
  };

  const handleEndCall = async () => {
    setCallState("ending");
    try {
      if (socket && socket.connected) {
        socket.emit("call:end", { ticketId: ticket.id });
      } else {
        await api.post(`/tickets/${ticket.id}/call/end`);
      }
    } catch (err) {
      // Chamada já encerrada no servidor não é erro para o operador.
      if (err?.response?.status !== 404) {
        toastError(err);
      }
    } finally {
      await resetCallState();
    }
  };

  const handleStartBaileysCall = async () => {
    const phone = onlyDigits(ticket?.contact?.number);

    if (!phone) {
      toastError("Não foi possível iniciar a chamada: número do contato inválido.");
      return;
    }

    setCalling(true);
    setElapsed(0);
    mutedRef.current = false;
    setMuted(false);
    setCallState("starting");
    let micStream;
    let callStarted = false;

    try {
      micStream = await acquireMicrophone();
      await waitForSocket();

      let response;
      try {
        response = await api.post(`/tickets/${ticket.id}/call`);
      } catch (startErr) {
        const errorCode = startErr?.response?.data?.error;

        if (errorCode === "ERR_CALL_ALREADY_ACTIVE_GLOBAL") {
          throw new Error(
            "Já existe uma ligação em andamento no sistema. Aguarde ela terminar para ligar."
          );
        }

        // Sobrou uma chamada do backend: encerra e refaz para não travar o botão.
        if (startErr?.response?.status === 409) {
          await api.post(`/tickets/${ticket.id}/call/end`);
          response = await api.post(`/tickets/${ticket.id}/call`);
        } else {
          throw startErr;
        }
      }

      const { data } = response;
      callStarted = true;
      setCallState("starting");
      setCallId(data?.callId || null);

      await startMicrophoneStreaming(micStream);
    } catch (err) {
      await resetCallState();

      if (callStarted) {
        try {
          await api.post(`/tickets/${ticket.id}/call/end`);
        } catch (endErr) {
          // noop
        }
      }

      if (err?.response) {
        toastError(err);
      } else if (err?.name === "NotAllowedError") {
        toast.error(
          "Permissão do microfone negada. Libere o acesso ao microfone para ligar."
        );
      } else if (err?.name === "NotFoundError") {
        toast.error("Nenhum microfone encontrado neste dispositivo.");
      } else {
        toast.error(err?.message || "Não foi possível acessar o microfone.");
      }
    }
  };

  useEffect(() => {
    if (!socket || !ticket?.id) return undefined;

    const handleCallEvent = async data => {
      if (!data || Number(data.ticketId) !== Number(ticket.id)) return;

      if (data.type === "audio") {
        try {
          playRemoteChunk(data.chunk);
        } catch (err) {
          // Um chunk inválido não pode derrubar o restante do controle da chamada.
        }
        return;
      }

      if (data.type === "started") {
        setCalling(true);
        setCallState("starting");
        if (data.callId) setCallId(data.callId);
        return;
      }

      if (data.type === "ringing") {
        setCallState("ringing");
        return;
      }

      if (data.type === "connected") {
        setCallState("connected");
        setElapsed(0);
        return;
      }

      if (data.type === "error") {
        toast.error(data.message || "Erro na chamada");
        await resetCallState();
        return;
      }

      if (data.type === "ended") {
        if (data.reason === "no_answer") {
          toast.info("O contato não atendeu.");
        } else if (data.reason === "remote_end") {
          toast.info("Chamada encerrada pelo contato.");
        }
        await resetCallState();
      }
    };

    socket.on("call", handleCallEvent);

    // Se o servidor cair, o evento "ended" nunca chega e o painel ficaria preso.
    const handleSocketDisconnect = () => {
      if (callStateRef.current === "idle") return;
      toast.error("Conexão com o servidor perdida. Chamada encerrada.");
      resetCallState();
    };

    socket.on("disconnect", handleSocketDisconnect);

    return () => {
      socket.off("call", handleCallEvent);
      socket.off("disconnect", handleSocketDisconnect);
    };
  }, [socket, ticket?.id]);

  useEffect(() => {
    if (callState !== "connected") return undefined;

    const startedAt = Date.now();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [callState]);

  useEffect(() => {
    return () => {
      stopMicrophoneStreaming();
      if (outputContextRef.current) {
        outputContextRef.current.close();
        outputContextRef.current = null;
      }
    };
  }, []);

  const callStatusLabel = {
    starting: "Conectando...",
    ringing: "Chamando...",
    connected: formatDuration(elapsed),
    ending: "Encerrando...",
  }[callState];

  return (
    <div className={classes.actionButtons}>
      {/* Botão de ligação via backend (baileys-caller compat) */}
      {!ticket?.isGroup && (
        <>
          {!calling ? (
            <Tooltip title="Ligar">
              <IconButton color="secondary" onClick={handleStartBaileysCall}>
                <Phone />
              </IconButton>
            </Tooltip>
          ) : (
            <span
              className={classes.callPanel}
              title={callId ? `Chamada ${callId}` : undefined}
            >
              <Chip
                size="small"
                icon={<Phone fontSize="small" />}
                label={callStatusLabel || "Em chamada"}
                color={callState === "connected" ? "primary" : "default"}
                className={`${classes.callChip} ${
                  callState === "ringing" ? classes.ringing : ""
                }`}
              />
              <Tooltip title={muted ? "Ativar microfone" : "Silenciar microfone"}>
                <IconButton size="small" onClick={handleToggleMute}>
                  {muted ? <MicOff fontSize="small" /> : <Mic fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Encerrar ligação">
                <span>
                  <IconButton
                    size="small"
                    className={classes.endCallButton}
                    onClick={handleEndCall}
                    disabled={callState === "ending"}
                  >
                    <CallEnd fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </span>
          )}
        </>
      )}

      {ticket.status === "closed" && (
        <ButtonWithSpinner
          loading={loading}
          startIcon={<Replay />}
          size="small"
          onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
        >
          {i18n.t("messagesList.header.buttons.reopen")}
        </ButtonWithSpinner>
      )}

      {ticket.status === "open" && (
        <>
          <ButtonWithSpinner
            loading={loading}
            startIcon={<Replay />}
            size="small"
            onClick={e => handleUpdateTicketStatus(e, "pending", null)}
          >
            {i18n.t("messagesList.header.buttons.return")}
          </ButtonWithSpinner>
          <ButtonWithSpinner
            loading={loading}
            size="small"
            variant="contained"
            color="primary"
            onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
          >
            {i18n.t("messagesList.header.buttons.resolve")}
          </ButtonWithSpinner>
          <IconButton onClick={handleOpenTicketOptionsMenu}>
            <MoreVert />
          </IconButton>
          <TicketOptionsMenu
            ticket={ticket}
            anchorEl={anchorEl}
            menuOpen={ticketOptionsMenuOpen}
            handleClose={handleCloseTicketOptionsMenu}
          />
        </>
      )}

      {ticket.status === "pending" && (
        <ButtonWithSpinner
          loading={loading}
          size="small"
          variant="contained"
          color="primary"
          onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
        >
          {i18n.t("messagesList.header.buttons.accept")}
        </ButtonWithSpinner>
      )}
    </div>
  );
};

export default TicketActionButtons;
